import { useEffect, useRef, useState } from "react";

import { api } from "./api";
import type { StreamEvent } from "./types";

type Status = "connecting" | "live" | "offline";

/**
 * SSE on /api/v1/stream. Reconnects with backoff; the browser does most of that for us.
 *
 * Each connection is opened with a fresh single-use ticket rather than the device token,
 * because EventSource can only authenticate through the query string and a query string ends
 * up in proxy logs and browser history. A ticket that has already been redeemed and expires in
 * a minute is worth nothing to whoever reads it there; the device token never expires.
 *
 * If the ticket request itself fails -- server mid-restart, say -- it falls back to the token,
 * which the server still accepts. A live feed matters more than the tidier credential.
 */
export function useStream(onEvent: (event: StreamEvent) => void) {
  const [status, setStatus] = useState<Status>("connecting");
  const handler = useRef(onEvent);
  handler.current = onEvent;

  useEffect(() => {
    let source: EventSource | null = null;
    let retry: number | undefined;
    let attempts = 0;
    let closed = false;

    const scheduleRetry = () => {
      if (closed) return;
      setStatus("offline");
      attempts += 1;
      retry = window.setTimeout(connect, Math.min(30_000, 1000 * 2 ** attempts));
    };

    const connect = async () => {
      if (closed) return;
      let url: string;
      try {
        const { ticket } = await api.streamTicket();
        url = api.streamUrlWithTicket(ticket);
      } catch {
        url = api.streamUrl();
      }
      if (closed) return;

      source = new EventSource(url);
      source.onopen = () => {
        attempts = 0;
        setStatus("live");
      };
      source.onmessage = (event) => {
        try {
          handler.current(JSON.parse(event.data) as StreamEvent);
        } catch {
          /* a malformed frame is not worth tearing the feed down for */
        }
      };
      for (const name of ["match", "poll", "budget_warning", "hello"]) {
        source.addEventListener(name, (event) => {
          try {
            handler.current(JSON.parse((event as MessageEvent).data) as StreamEvent);
          } catch {
            /* ignore */
          }
        });
      }
      source.onerror = () => {
        // A ticket is single-use, so the browser's own silent reconnect would replay a spent
        // one and get a 401 forever. Close it and reconnect ourselves with a new ticket.
        source?.close();
        scheduleRetry();
      };
    };

    void connect();
    return () => {
      closed = true;
      window.clearTimeout(retry);
      source?.close();
    };
  }, []);

  return status;
}
